import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, MapPin, Utensils, ShoppingBag, Heart, Car, CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

const Guide = () => {
  const guideFeatures = [
    { icon: Utensils, title: 'Dining & Restaurants', description: 'Local favorites, hidden gems, and the best eats in town' },
    { icon: ShoppingBag, title: 'Shopping & Retail', description: 'From boutiques to big-box stores, find where to shop' },
    { icon: Heart, title: 'Healthcare & Wellness', description: 'Hospitals, clinics, gyms, and wellness centers nearby' },
    { icon: MapPin, title: 'Neighborhood Guides', description: 'In-depth looks at Memphis, Southaven, Olive Branch & more' },
    { icon: Car, title: 'Getting Around', description: 'Transportation, commute times, and local tips' },
    { icon: CheckCircle, title: 'Local Services', description: 'Everything from banks to pet care to home services' },
  ];

  const coveredAreas = [
    'Memphis, TN',
    'Germantown, TN',
    'Collierville, TN',
    'Southaven, MS',
    'Olive Branch, MS',
    'Hernando, MS',
    'Byhalia, MS',
    'Horn Lake, MS'
  ];

  return (
    <div className="min-h-screen" data-testid="guide-page">
      <SEO 
        title="MS/TN Local Guide - Dining, Shopping & More"
        description="Your curated guide to dining, shopping, healthcare, and more in Olive Branch, Southaven, Memphis, and surrounding areas. Discover the best local spots."
        keywords="Memphis local guide, Southaven restaurants, Olive Branch shopping, North Mississippi dining, Tennessee healthcare"
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#434345] to-[#5a5a5c] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#BCA480] rounded-full mb-6">
              <BookOpen className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="guide-heading">
              Discover MS/TN Like a Local
            </h1>
            <p className="text-xl sm:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Your curated guide to dining, shopping, healthcare, and more in Olive Branch, Southaven, Memphis, and surrounding areas
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#BCA480] hover:bg-[#a89470] text-white px-8 py-6 text-lg"
              data-testid="access-guide-btn"
            >
              <a href="https://mstn-guide.emergent.host" target="_blank" rel="noopener noreferrer">
                Explore the Guide
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#434345] mb-4">What You&apos;ll Find</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Local insights to help you feel at home in the Mid-South region
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guideFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#BCA480]/10 rounded-lg mb-4">
                  <feature.icon className="h-6 w-6 text-[#BCA480]" />
                </div>
                <h3 className="text-xl font-bold text-[#434345] mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#434345] mb-4">Areas Covered</h2>
            <p className="text-lg text-gray-600">
              Detailed information for these communities and surrounding areas
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {coveredAreas.map((area, index) => (
              <div 
                key={index}
                className="bg-white px-6 py-3 rounded-full shadow-sm border border-gray-200 flex items-center gap-2"
              >
                <MapPin className="h-4 w-4 text-[#BCA480]" />
                <span className="font-medium text-[#434345]">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Use Our Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#BCA480]/10 to-white rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#434345] mb-4">Why You&apos;ll Love It</h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#BCA480] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Local Favorites:</strong> Curated by people who actually live here</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#BCA480] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Always Fresh:</strong> Regularly updated with new spots and info</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#BCA480] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Easy to Browse:</strong> Find what you need quickly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-[#BCA480] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Totally Free:</strong> No signup, no email required</span>
                  </li>
                </ul>
              </div>
              <div className="text-center">
                <div className="bg-[#434345] rounded-xl p-8 text-white">
                  <p className="text-5xl font-bold text-[#BCA480] mb-2">100%</p>
                  <p className="text-xl mb-4">Local & Free</p>
                  <p className="text-white/80 text-sm mb-6">No signup required. Instant access.</p>
                  <Button 
                    asChild
                    className="bg-[#BCA480] hover:bg-[#a89470] text-white w-full"
                  >
                    <a href="https://mstn-guide.emergent.host" target="_blank" rel="noopener noreferrer">
                      Get the Guide
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#BCA480] to-[#a89470] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Explore?</h2>
          <p className="text-xl mb-8 text-white/90">
            Get instant access to our comprehensive guide and start planning your move today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-white text-[#434345] hover:bg-gray-100"
            >
              <a href="https://mstn-guide.emergent.host" target="_blank" rel="noopener noreferrer">
                Access Free Guide
              </a>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              <Link to="/contact">Talk to an Agent</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Guide;
